import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, ShieldCheck } from "lucide-react";

export default function SetPassword({ forced = false }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If not forced and user already changed password, don't stay on this screen.
    if (!forced && user && !user.must_change_password) {
      // still allow voluntary access — no redirect
    }
  }, [forced, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next === current) { setError("New password must differ from current."); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      if (data.access_token) localStorage.setItem("boardroom_token", data.access_token);
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="set-password-page">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="set-password-form">
        <div className="flex items-center gap-2 text-[#D4AF37] mb-6">
          <KeyRound size={16} />
          <p className="overline">
            {forced ? "First-Time Sign In" : "Change Password"}
          </p>
        </div>
        <h2 className="font-display text-4xl mb-2">
          {forced ? "Set your permanent password." : "Change your password."}
        </h2>
        <p className="text-sm text-neutral-500 mb-8 font-body">
          {forced
            ? "Your invite included a temporary password. Choose a new one before entering The Boardroom."
            : "Choose a new password. All active sessions on other devices will be signed out."}
        </p>

        <label className="overline mb-2 block">
          {forced ? "Temporary Password" : "Current Password"}
        </label>
        <input type="password" required autoFocus
               value={current} onChange={(e) => setCurrent(e.target.value)}
               className="input-boardroom mb-6" data-testid="setpw-current" />

        <label className="overline mb-2 block">New Password</label>
        <input type="password" required minLength={8}
               value={next} onChange={(e) => setNext(e.target.value)}
               className="input-boardroom mb-6" data-testid="setpw-new"
               placeholder="Minimum 8 characters" />

        <label className="overline mb-2 block">Confirm New Password</label>
        <input type="password" required minLength={8}
               value={confirm} onChange={(e) => setConfirm(e.target.value)}
               className="input-boardroom mb-4" data-testid="setpw-confirm" />

        {error && (
          <div className="text-sm text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2 mb-4"
               data-testid="setpw-error">{error}</div>
        )}

        <button type="submit" disabled={busy} className="btn-gold w-full mt-4"
                data-testid="setpw-submit">
          <ShieldCheck size={14} className="inline mr-2" />
          {busy ? "Updating…" : forced ? "Set Password & Enter" : "Update Password"}
        </button>

        {forced && (
          <p className="text-[10px] text-neutral-600 mt-6 uppercase tracking-widest text-center">
            ◇ You cannot access the curriculum until this step is complete ◇
          </p>
        )}
      </form>
    </div>
  );
}
